import { useEffect, useState, useCallback, useRef } from "react";
import jsPDF from "jspdf";
import api, { formatDate, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { viewDoc, downloadDoc } from "../components/DocActions";
import {
  ClipboardList, Plus, Trash2, X, FileText, Upload, Eye, Download, Loader2,
  LogIn, LogOut, AlertTriangle, Zap, FileWarning, CheckCircle2, Layers, ChevronRight, Send,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";

const ITEM_STATUSES = ["Not Started", "In Progress", "Completed", "Not Applicable"];
const CATEGORIES = ["Move-In", "Move-Out", "Utility Transfer", "Maintenance Readiness"];

const statusTone = {
  "Not Started": "bg-muted text-muted-foreground",
  "In Progress": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  Completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Not Applicable": "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};
const catTone = {
  "Move-In": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40",
  "Move-Out": "bg-amber-100 text-amber-700 dark:bg-amber-900/40",
  "Utility Transfer": "bg-blue-100 text-blue-700 dark:bg-blue-900/40",
  "Maintenance Readiness": "bg-violet-100 text-violet-700 dark:bg-violet-900/40",
};

const isOverdue = (it) => it.status !== "Completed" && it.status !== "Not Applicable"
  && it.due_date && it.due_date.slice(0, 10) < new Date().toISOString().slice(0, 10);

function ProgressBar({ pct }) {
  return (
    <div className="h-2 rounded-full bg-muted overflow-hidden">
      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function Checklists() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager"].includes(user?.role);
  const [tab, setTab] = useState("checklists");
  const [alerts, setAlerts] = useState(null);
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [refs, setRefs] = useState({ properties: [], tenants: [], leases: [], users: [] });
  const [filters, setFilters] = useState({});
  const [newOpen, setNewOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [tplEdit, setTplEdit] = useState(null); // template object or {} for new
  const [autoSend, setAutoSend] = useState(false);

  useEffect(() => { api.get("/checklist-settings").then((r) => setAutoSend(r.data.auto_send_welcome_packet)).catch(() => {}); }, []);
  const toggleAutoSend = async (v) => {
    setAutoSend(v);
    try { await api.put("/checklist-settings", { auto_send_welcome_packet: v }); toast.success(v ? "Auto-send enabled" : "Auto-send disabled"); }
    catch (e) { setAutoSend(!v); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadRefs = useCallback(() => {
    Promise.all([
      api.get("/properties").catch(() => ({ data: [] })),
      api.get("/tenants").catch(() => ({ data: [] })),
      api.get("/leases").catch(() => ({ data: [] })),
      api.get("/checklist-users").catch(() => ({ data: [] })),
    ]).then(([p, t, l, u]) => setRefs({ properties: p.data, tenants: t.data, leases: l.data, users: u.data }));
  }, []);

  const loadTemplates = useCallback(() => {
    api.get("/checklist-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString() ? `?${p.toString()}` : "";
  }, [filters]);

  const loadList = useCallback(() => {
    api.get(`/checklists${qs()}`).then((r) => setRows(r.data)).catch(() => {});
    api.get("/checklists/dashboard/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, [qs]);

  useEffect(() => { loadRefs(); loadTemplates(); }, [loadRefs, loadTemplates]);
  useEffect(() => { loadList(); }, [loadList]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v || undefined }));

  const alertCards = alerts ? [
    { k: "move_ins_soon", label: "Move-Ins Soon", icon: LogIn, tone: "text-emerald-600", show: alerts.move_ins_soon },
    { k: "move_outs_soon", label: "Move-Outs Soon", icon: LogOut, tone: "text-amber-600", show: alerts.move_outs_soon },
    { k: "incomplete_movein", label: "Incomplete Move-Ins", icon: FileWarning, tone: "text-blue-600", show: alerts.incomplete_movein },
    { k: "overdue_tasks", label: "Overdue Tasks", icon: AlertTriangle, tone: "text-red-600", show: alerts.overdue_tasks },
    { k: "utility_pending", label: "Utility Transfers Pending", icon: Zap, tone: "text-violet-600", show: alerts.utility_pending },
    { k: "missing_docs", label: "Missing Docs", icon: FileText, tone: "text-orange-600", show: alerts.missing_docs },
  ] : [];

  return (
    <div className="space-y-6" data-testid="page-checklists">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Leasing Operations</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><ClipboardList className="w-7 h-7 text-primary" /> Move-In / Move-Out Checklists</h1>
          <p className="text-sm text-muted-foreground mt-1">Track turnovers, assign tasks, upload photos & docs, and generate welcome packets.</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer" title="Automatically email the welcome packet to the tenant when a Move-In checklist is created">
              <Switch checked={autoSend} onCheckedChange={toggleAutoSend} data-testid="auto-send-toggle" />
              Auto-email packet on move-in
            </label>
            <Button onClick={() => setNewOpen(true)} data-testid="new-checklist-btn" className="rounded-xl"><Plus className="w-4 h-4 mr-1.5" /> New Checklist</Button>
          </div>
        )}
      </div>

      {/* Alerts strip */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="checklist-alerts">
        <div className="flex flex-wrap gap-3">
          {alertCards.filter((c) => c.show > 0).length === 0 ? (
            <p className="text-sm text-emerald-600 font-medium flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> All checklist tasks are on track.</p>
          ) : alertCards.filter((c) => c.show > 0).map((c) => (
            <div key={c.k} data-testid={`checklist-alert-${c.k}`} className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-2.5">
              <c.icon className={`w-4.5 h-4.5 ${c.tone}`} strokeWidth={1.75} />
              <div>
                <p className={`font-heading text-lg font-bold leading-none ${c.tone}`}>{c.show}</p>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mt-1">{c.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {[["checklists", "Checklists"], ["templates", "Templates"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "checklists" ? (
        <>
          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Sel testid="filter-property" value={filters.property_id} onChange={(v) => setF("property_id", v)} options={[["", "All properties"], ...refs.properties.map((p) => [p.id, p.name])]} />
            <Sel testid="filter-tenant" value={filters.tenant_id} onChange={(v) => setF("tenant_id", v)} options={[["", "All tenants"], ...refs.tenants.map((t) => [t.id, t.name])]} />
            <Sel testid="filter-category" value={filters.category} onChange={(v) => setF("category", v)} options={[["", "All categories"], ...CATEGORIES.map((c) => [c, c])]} />
            <Sel testid="filter-status" value={filters.status} onChange={(v) => setF("status", v)} options={[["", "All statuses"], ...["Active", "Completed", "Archived"].map((c) => [c, c])]} />
          </div>

          {rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
              No checklists yet. {canWrite && "Click \u201cNew Checklist\u201d to create one."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {rows.map((c) => (
                <button key={c.id} onClick={() => setDetailId(c.id)} data-testid={`checklist-card-${c.id}`}
                  className="text-left rounded-2xl border border-border bg-card p-5 hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5 transition-all">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Badge className={`rounded-sm font-normal ${catTone[c.category] || "bg-muted"}`}>{c.category}</Badge>
                    {c.overdue_count > 0 && <Badge className="rounded-sm bg-red-100 text-red-700 dark:bg-red-900/40">{c.overdue_count} overdue</Badge>}
                  </div>
                  <p className="font-heading font-semibold truncate">{c.name}</p>
                  <p className="text-sm text-muted-foreground truncate mt-0.5">{c.property_name}{c.unit ? ` · ${c.unit}` : ""}</p>
                  <p className="text-sm text-muted-foreground truncate">{c.tenant_name || "No tenant"}</p>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Progress</span><span>{c.progress}%</span></div>
                    <ProgressBar pct={c.progress} />
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                    <span>{c.category === "Move-Out" ? `Out: ${formatDate(c.move_out_date)}` : `In: ${formatDate(c.move_in_date)}`}</span>
                    <span className="inline-flex items-center gap-1 text-primary font-medium">Open <ChevronRight className="w-3.5 h-3.5" /></span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <TemplatesTab templates={templates} canWrite={canWrite} onEdit={setTplEdit} reload={loadTemplates} />
      )}

      {newOpen && <NewChecklistDialog refs={refs} templates={templates} autoSendDefault={autoSend} onClose={() => setNewOpen(false)} onCreated={(id) => { setNewOpen(false); loadList(); setDetailId(id); }} />}
      {detailId && <DetailDialog cid={detailId} canWrite={canWrite} users={refs.users} onClose={() => { setDetailId(null); loadList(); }} />}
      {tplEdit !== null && <TemplateDialog tpl={tplEdit} onClose={() => setTplEdit(null)} onSaved={() => { setTplEdit(null); loadTemplates(); }} />}
    </div>
  );
}

function Sel({ testid, value, onChange, options }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-lg border border-border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

// ---------------- New Checklist ----------------
function NewChecklistDialog({ refs, templates, autoSendDefault, onClose, onCreated }) {
  const [f, setF] = useState({ category: "Move-In", template_id: "", property_id: "", lease_id: "", unit: "", tenant_id: "", move_in_date: "", move_out_date: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [emailNow, setEmailNow] = useState(!!autoSendDefault);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const catTemplates = templates.filter((t) => t.category === f.category);

  const autoSendPacket = async (cid) => {
    try {
      const { data } = await api.get(`/checklists/${cid}/welcome-packet`);
      const pdf = makeWelcomePacketDoc(data).output("datauristring");
      const filename = `welcome-packet-${(data.tenant_name || "tenant").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      const { data: res } = await api.post(`/checklists/${cid}/send-welcome-packet`, { pdf_base64: pdf, filename });
      toast.success(`Welcome packet emailed to ${res.email}`);
    } catch (e) {
      toast.warning(`Checklist created, but packet not emailed: ${formatApiErrorDetail(e.response?.data?.detail)}`);
    }
  };

  const create = async () => {
    if (!f.property_id && !f.lease_id) { toast.error("Select a property or a lease."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/checklists", f);
      toast.success("Checklist created");
      if (emailNow && f.category === "Move-In") await autoSendPacket(data.id);
      onCreated(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="new-checklist-dialog">
        <DialogHeader><DialogTitle>New Checklist</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <Fld label="Category">
            <Sel testid="nc-category" value={f.category} onChange={(v) => { set("category", v); set("template_id", ""); }} options={CATEGORIES.map((c) => [c, c])} />
          </Fld>
          <Fld label="Template (auto if blank)">
            <Sel testid="nc-template" value={f.template_id} onChange={(v) => set("template_id", v)} options={[["", "Auto-select by property type"], ...catTemplates.map((t) => [t.id, t.name])]} />
          </Fld>
          <Fld label="Lease (optional — auto-fills)">
            <Sel testid="nc-lease" value={f.lease_id} onChange={(v) => set("lease_id", v)} options={[["", "None"], ...refs.leases.map((l) => [l.id, `${l.property_name} · ${l.unit || ""} · ${l.tenant_name || ""}`])]} />
          </Fld>
          <Fld label="Property">
            <Sel testid="nc-property" value={f.property_id} onChange={(v) => set("property_id", v)} options={[["", "Select property"], ...refs.properties.map((p) => [p.id, p.name])]} />
          </Fld>
          <Fld label="Unit"><input value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="nc-unit" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <Fld label="Tenant">
            <Sel testid="nc-tenant" value={f.tenant_id} onChange={(v) => set("tenant_id", v)} options={[["", "None"], ...refs.tenants.map((t) => [t.id, t.name])]} />
          </Fld>
          {f.category === "Move-Out"
            ? <Fld label="Move-Out Date"><input type="date" value={f.move_out_date} onChange={(e) => set("move_out_date", e.target.value)} data-testid="nc-moveout" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
            : <Fld label="Move-In Date"><input type="date" value={f.move_in_date} onChange={(e) => set("move_in_date", e.target.value)} data-testid="nc-movein" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>}
          <Fld label="Name (optional)"><input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="nc-name" className="w-full rounded-lg border border-border bg-background p-2 text-sm" placeholder="Defaults to template name" /></Fld>
        </div>
        {f.category === "Move-In" && (
          <label className="flex items-center gap-2 text-sm cursor-pointer px-1" data-testid="nc-email-now-label">
            <Checkbox checked={emailNow} onCheckedChange={(v) => setEmailNow(!!v)} data-testid="nc-email-now" />
            Email the welcome packet to the tenant on creation
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          <Button className="rounded-xl" onClick={create} disabled={saving} data-testid="nc-save">{saving ? "Creating…" : "Create Checklist"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children }) {
  return (<div><label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>{children}</div>);
}

// ---------------- Detail ----------------
function DetailDialog({ cid, canWrite, users, onClose }) {
  const [c, setC] = useState(null);
  const [newItem, setNewItem] = useState("");
  const [gen, setGen] = useState(false);
  const [sending, setSending] = useState(false);
  const inputs = useRef({});

  const load = useCallback(() => { api.get(`/checklists/${cid}`).then((r) => setC(r.data)).catch(() => {}); }, [cid]);
  useEffect(() => { load(); }, [load]);

  const patchItem = async (itemId, body) => {
    try { const { data } = await api.put(`/checklists/${cid}/items/${itemId}`, body); setC(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addItem = async () => {
    if (!newItem.trim()) return;
    try { const { data } = await api.post(`/checklists/${cid}/items`, { title: newItem.trim() }); setC(data); setNewItem(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delItem = async (itemId) => {
    try { const { data } = await api.delete(`/checklists/${cid}/items/${itemId}`); setC(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const upload = (itemId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/checklists/${cid}/items/${itemId}/attachment`, {
          name: file.name, file_type: file.name.split(".").pop().toLowerCase(),
          size_kb: Math.round(file.size / 1024), data: reader.result,
        });
        setC(data); toast.success("File attached");
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const removeDoc = async (itemId) => {
    try { const { data } = await api.delete(`/checklists/${cid}/items/${itemId}/attachment`); setC(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteChecklist = async () => {
    if (!window.confirm("Delete this checklist permanently?")) return;
    try { await api.delete(`/checklists/${cid}`); toast.success("Checklist deleted"); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const genPacket = async () => {
    setGen(true);
    try {
      const { data } = await api.get(`/checklists/${cid}/welcome-packet`);
      buildWelcomePacketPDF(data);
    } catch (e) { toast.error("Could not build welcome packet"); }
    finally { setGen(false); }
  };

  const emailPacket = async () => {
    setSending(true);
    try {
      const { data } = await api.get(`/checklists/${cid}/welcome-packet`);
      const pdf = makeWelcomePacketDoc(data).output("datauristring");
      const filename = `welcome-packet-${(data.tenant_name || "tenant").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      const { data: res } = await api.post(`/checklists/${cid}/send-welcome-packet`, { pdf_base64: pdf, filename });
      toast.success(`Welcome packet emailed to ${res.email}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!c) return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>Loading checklist…</DialogTitle></DialogHeader><div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div></DialogContent></Dialog>
  );

  const done = c.items.filter((i) => i.status === "Completed" || i.status === "Not Applicable").length;
  const pct = c.items.length ? Math.round(done / c.items.length * 100) : 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="checklist-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Badge className={`rounded-sm font-normal ${catTone[c.category] || "bg-muted"}`}>{c.category}</Badge>
            {c.name}
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-muted-foreground -mt-1">
          {c.property_name}{c.unit ? ` · ${c.unit}` : ""} · {c.tenant_name || "No tenant"}
          {c.move_in_date && ` · Move-in ${formatDate(c.move_in_date)}`}
          {c.move_out_date && ` · Move-out ${formatDate(c.move_out_date)}`}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1"><ProgressBar pct={pct} /></div>
          <span className="text-xs font-medium text-muted-foreground">{done}/{c.items.length} done ({pct}%)</span>
        </div>

        <div className="flex flex-wrap gap-2">
          {c.category === "Move-In" && <Button variant="outline" className="rounded-xl" onClick={genPacket} disabled={gen} data-testid="welcome-packet-btn">{gen ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />} Welcome Packet PDF</Button>}
          {c.category === "Move-In" && canWrite && <Button variant="outline" className="rounded-xl" onClick={emailPacket} disabled={sending} data-testid="email-packet-btn">{sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Email to Tenant</Button>}
          {canWrite && (
            <Sel testid="checklist-status" value={c.status} onChange={async (v) => { const { data } = await api.put(`/checklists/${cid}`, { status: v }); setC(data); }}
              options={["Active", "Completed", "Archived"].map((s) => [s, s])} />
          )}
          {canWrite && <Button variant="outline" className="rounded-xl text-red-600 hover:text-red-700 ml-auto" onClick={deleteChecklist} data-testid="delete-checklist-btn"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>}
        </div>

        <div className="space-y-2 mt-2">
          {c.items.map((it) => (
            <div key={it.id} data-testid={`item-${it.id}`} className={`rounded-xl border p-3 ${isOverdue(it) ? "border-red-300 bg-red-50/40 dark:bg-red-900/10" : "border-border bg-card"}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${it.status === "Completed" ? "line-through text-muted-foreground" : ""}`}>{it.title}</p>
                  {it.completed_date && <p className="text-[11px] text-emerald-600 mt-0.5">Completed {formatDate(it.completed_date)}{it.completed_by ? ` · ${it.completed_by}` : ""}</p>}
                </div>
                {canWrite && (
                  <select value={it.status} onChange={(e) => patchItem(it.id, { status: e.target.value })} data-testid={`item-status-${it.id}`}
                    className={`rounded-lg text-xs font-medium px-2 py-1.5 border-0 ${statusTone[it.status]}`}>
                    {ITEM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                {!canWrite && <Badge className={`rounded-sm font-normal ${statusTone[it.status]}`}>{it.status}</Badge>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Due</label>
                  <input type="date" disabled={!canWrite} value={it.due_date || ""} onChange={(e) => patchItem(it.id, { due_date: e.target.value })} data-testid={`item-due-${it.id}`}
                    className={`w-full rounded-lg border border-border bg-background p-1.5 text-xs ${isOverdue(it) ? "text-red-600 font-semibold" : ""}`} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Assigned To</label>
                  <select disabled={!canWrite} value={it.assigned_to || ""} data-testid={`item-assignee-${it.id}`}
                    onChange={(e) => { const u = users.find((x) => x.id === e.target.value); patchItem(it.id, { assigned_to: e.target.value, assigned_name: u?.name || "" }); }}
                    className="w-full rounded-lg border border-border bg-background p-1.5 text-xs">
                    <option value="">Unassigned</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Attachment</label>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <input ref={(el) => (inputs.current[it.id] = el)} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      onChange={(e) => { upload(it.id, e.target.files[0]); e.target.value = ""; }} data-testid={`item-fileinput-${it.id}`} />
                    {it.doc_id ? (
                      <>
                        <button onClick={() => viewDoc(it.doc_id)} data-testid={`item-view-${it.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20"><Eye className="w-3.5 h-3.5" /> View</button>
                        <button onClick={() => downloadDoc(it.doc_id)} className="text-muted-foreground hover:text-primary" title="Download"><Download className="w-3.5 h-3.5" /></button>
                        {canWrite && <button onClick={() => removeDoc(it.id)} className="text-muted-foreground hover:text-red-500" title="Remove" data-testid={`item-rmdoc-${it.id}`}><X className="w-3.5 h-3.5" /></button>}
                      </>
                    ) : canWrite ? (
                      <button onClick={() => inputs.current[it.id]?.click()} data-testid={`item-upload-${it.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary"><Upload className="w-3.5 h-3.5" /> Upload</button>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <input disabled={!canWrite} value={it.notes || ""} onChange={(e) => patchItem(it.id, { notes: e.target.value })} data-testid={`item-notes-${it.id}`}
                  placeholder="Notes…" className="flex-1 rounded-lg border border-border bg-background p-1.5 text-xs" />
                {canWrite && <button onClick={() => delItem(it.id)} data-testid={`item-delete-${it.id}`} className="text-muted-foreground hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>

        {canWrite && (
          <div className="flex items-center gap-2 mt-2">
            <input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} data-testid="new-item-input"
              placeholder="Add a custom task…" className="flex-1 rounded-lg border border-border bg-background p-2 text-sm" />
            <Button variant="outline" className="rounded-xl" onClick={addItem} data-testid="add-item-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Templates ----------------
function TemplatesTab({ templates, canWrite, onEdit, reload }) {
  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/checklist-templates/${id}`); toast.success("Template deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      {canWrite && <Button onClick={() => onEdit({})} data-testid="new-template-btn" className="rounded-xl"><Plus className="w-4 h-4 mr-1.5" /> New Template</Button>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-${t.id}`}
            onClick={canWrite ? () => onEdit(t) : undefined} role={canWrite ? "button" : undefined} tabIndex={canWrite ? 0 : undefined}
            onKeyDown={canWrite ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEdit(t); } } : undefined}
            className={`rounded-2xl border border-border bg-card p-5 transition-all ${canWrite ? "cursor-pointer hover:border-primary/40 hover:shadow-sm" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-heading font-semibold flex items-center gap-2"><Layers className="w-4 h-4 text-primary" /> {t.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`rounded-sm font-normal ${catTone[t.category] || "bg-muted"}`}>{t.category}</Badge>
                  {t.is_default && <span className="text-[11px] text-muted-foreground">Default</span>}
                </div>
              </div>
              {canWrite && (
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); onEdit(t); }} data-testid={`edit-template-${t.id}`} className="text-xs text-primary hover:underline">Edit</button>
                  <button onClick={(e) => { e.stopPropagation(); del(t.id); }} data-testid={`delete-template-${t.id}`} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            {t.property_types?.length > 0 && <p className="text-xs text-muted-foreground mt-2">For: {t.property_types.join(", ")}</p>}
            <p className="text-xs text-muted-foreground mt-2">{t.items.length} tasks</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateDialog({ tpl, onClose, onSaved }) {
  const isNew = !tpl.id;
  const [name, setName] = useState(tpl.name || "");
  const [category, setCategory] = useState(tpl.category || "Move-In");
  const [propTypes, setPropTypes] = useState((tpl.property_types || []).join(", "));
  const [items, setItems] = useState((tpl.items || []).map((i) => i.title));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const titles = items.map((t) => t.trim()).filter(Boolean);
    if (!name.trim() || titles.length === 0) { toast.error("Name and at least one task required."); return; }
    setSaving(true);
    const property_types = propTypes.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      if (isNew) {
        await api.post("/checklist-templates", { name, category, property_types, items: titles });
      } else {
        await api.put(`/checklist-templates/${tpl.id}`, { name, category, property_types, items: titles.map((t, i) => ({ title: t, order: i })) });
      }
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New Template" : "Edit Template"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <Fld label="Name"><input value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Category"><Sel testid="tpl-category" value={category} onChange={setCategory} options={["Move-In", "Move-Out", "Utility Transfer", "Maintenance Readiness"].map((c) => [c, c])} /></Fld>
            <Fld label="Property Types (comma-sep)"><input value={propTypes} onChange={(e) => setPropTypes(e.target.value)} data-testid="tpl-types" placeholder="Single Family, Condo" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Tasks</label>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={it} onChange={(e) => setItems((a) => a.map((x, j) => j === i ? e.target.value : x))} data-testid={`tpl-item-${i}`} className="flex-1 rounded-lg border border-border bg-background p-2 text-sm" />
                  <button onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <Button variant="outline" className="rounded-xl w-full" onClick={() => setItems((a) => [...a, ""])} data-testid="tpl-add-item"><Plus className="w-4 h-4 mr-1.5" /> Add Task</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="tpl-save">{saving ? "Saving…" : "Save Template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Welcome Packet PDF ----------------
function makeWelcomePacketDoc(d) {
  const doc = new jsPDF();
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, W, 34, "F");
  doc.setTextColor(255);
  doc.setFontSize(20);
  doc.text("Welcome Home!", 14, 20);
  doc.setFontSize(10);
  doc.text("Kora Holding, LLC — Tenant Welcome Packet", 14, 28);

  doc.setTextColor(30);
  let y = 46;
  doc.setFontSize(12);
  doc.text(`Dear ${d.tenant_name || "Tenant"},`, 14, y); y += 8;
  doc.setFontSize(10);
  doc.setTextColor(80);
  const intro = doc.splitTextToSize(
    `Welcome to your new home at ${d.address || d.property_name || "your new residence"}${d.unit ? `, ${d.unit}` : ""}. ` +
    `Below is important information to help you settle in${d.move_in_date ? ` starting ${formatDate(d.move_in_date)}` : ""}.`, W - 28);
  doc.text(intro, 14, y); y += intro.length * 5 + 4;

  const rows = [
    ["Electricity", d.utility_electric], ["Water", d.utility_water], ["Gas", d.utility_gas],
    ["Internet / Cable", d.internet_provider], ["Trash Pickup", d.trash_days], ["Recycling", d.recycling_info],
    ["Lawn / Landscaping", d.lawn_info], ["Pool Service", d.pool_info], ["Emergency Contact", d.emergency_contact],
  ].filter(([, v]) => v);

  const section = (title) => {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setTextColor(37, 99, 235); doc.setFontSize(12); doc.text(title, 14, y); y += 3;
    doc.setDrawColor(220); doc.line(14, y, W - 14, y); y += 6; doc.setTextColor(30); doc.setFontSize(10);
  };

  if (rows.length) {
    section("Utilities & Services");
    rows.forEach(([k, v]) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.setFont(undefined, "bold"); doc.text(`${k}:`, 14, y); doc.setFont(undefined, "normal");
      const val = doc.splitTextToSize(String(v), W - 70); doc.text(val, 60, y); y += Math.max(val.length * 5, 6);
    });
    y += 4;
  }
  if (d.rent_instructions) {
    section("Rent Payment Instructions");
    const t = doc.splitTextToSize(String(d.rent_instructions), W - 28); doc.text(t, 14, y); y += t.length * 5 + 4;
  }
  if (d.property_rules) {
    section("House Rules");
    const t = doc.splitTextToSize(String(d.property_rules), W - 28); doc.text(t, 14, y); y += t.length * 5 + 4;
  }

  if (y > 260) { doc.addPage(); y = 20; }
  doc.setTextColor(120); doc.setFontSize(9);
  doc.text("We're glad to have you. Please keep this packet for your records.", 14, y + 6);
  return doc;
}

function buildWelcomePacketPDF(d) {
  makeWelcomePacketDoc(d).save(`welcome-packet-${(d.tenant_name || "tenant").replace(/\s+/g, "-").toLowerCase()}.pdf`);
}
